import { useQuery } from '@tanstack/react-query'
import { swimApi } from '@swim/api-client'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { useSession } from '@/hooks/use-session'

export interface DropTransaction {
  id: string
  type: 'PURCHASE' | 'BURN' | 'REISSUE'
  amount: number
  balanceBefore: number
  balanceAfter: number
  createdAt: string
  expiresAt: string | null
}

interface DropBalanceResponse {
  memberId: string
  balance: number
  updatedAt: string
}

/**
 * Fetches the current authenticated user's drops balance.
 *
 * @remarks
 * - Only enabled when user is authenticated
 * - Caches for 30 seconds (staleTime), then marked stale on refocus
 * - Returns null balance when no user is logged in
 *
 * @example
 * const { data: balance } = useDropsBalance()
 * console.log(balance?.balance) // number or undefined
 */
export function useDropsTransactions() {
  const { user } = useSession()
  return useQuery<{ transactions: DropTransaction[] }>({
    queryKey: QUERY_KEYS.drops.transactions(user?.id ?? ''),
    queryFn: async () => {
      const result = await swimApi.get<{ transactions: DropTransaction[] }>('/drops/transactions')
      if (!result.success) throw new Error(result.error)
      return result.data
    },
    enabled: Boolean(user),
    staleTime: 30_000,
  })
}

export function useDropsBalance() {
  const { user } = useSession()
  return useQuery<DropBalanceResponse>({
    queryKey: QUERY_KEYS.drops.balance(user?.id ?? ''),
    queryFn: async () => {
      const result = await swimApi.get<DropBalanceResponse>('/drops/balance')
      if (!result.success) throw new Error(result.error)
      return result.data
    },
    enabled: Boolean(user),
    staleTime: 30_000,
  })
}
